/**
 * API client for the Daily Puzzle API.
 * Handles fetch calls with proper error handling and typing.
 */

import type {
    DailyResponse,
    AttemptRequest,
    AttemptResponse,
    RevealRequest,
    RevealResponse,
    ArchiveResponse,
    RecoveryResponse,
    RestoreRequest,
    RestoreResponse,
} from '@daily-puzzle/shared';

// In production, use the Workers API URL; in dev, use the Vite proxy
const API_BASE = import.meta.env.VITE_API_URL || '/api/v1';

class ApiError extends Error {
    constructor(
        public status: number,
        public code: string,
        message: string
    ) {
        super(message);
        this.name = 'ApiError';
    }
}

async function handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new ApiError(
            response.status,
            body.error ?? 'unknown_error',
            body.message ?? 'An error occurred'
        );
    }
    return response.json();
}

export async function fetchDaily(date?: string): Promise<DailyResponse> {
    const url = date ? `${API_BASE}/daily?date=${date}` : `${API_BASE}/daily`;
    const response = await fetch(url, { credentials: 'include' });
    return handleResponse<DailyResponse>(response);
}

export async function submitAttempt(
    date: string,
    moveUci: string,
    idempotencyKey: string
): Promise<AttemptResponse> {
    const body: AttemptRequest = { date, move_uci: moveUci };
    const response = await fetch(`${API_BASE}/attempt`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Idempotency-Key': idempotencyKey,
        },
        credentials: 'include',
        body: JSON.stringify(body),
    });
    return handleResponse<AttemptResponse>(response);
}

export async function revealPuzzle(
    date: string,
    idempotencyKey: string
): Promise<RevealResponse> {
    const body: RevealRequest = { date };
    const response = await fetch(`${API_BASE}/reveal`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Idempotency-Key': idempotencyKey,
        },
        credentials: 'include',
        body: JSON.stringify(body),
    });
    return handleResponse<RevealResponse>(response);
}

export async function fetchArchive(): Promise<ArchiveResponse> {
    const response = await fetch(`${API_BASE}/archive`, { credentials: 'include' });
    return handleResponse<ArchiveResponse>(response);
}

export async function fetchRecoveryCode(): Promise<RecoveryResponse> {
    const response = await fetch(`${API_BASE}/recovery`, { credentials: 'include' });
    return handleResponse<RecoveryResponse>(response);
}

export async function restoreProgress(recoveryCode: string): Promise<RestoreResponse> {
    const body: RestoreRequest = { recovery_code: recoveryCode };
    const response = await fetch(`${API_BASE}/restore`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
    });
    return handleResponse<RestoreResponse>(response);
}

export { ApiError };
